import { workflowTrackBy } from '../common/employee-assets/asset.helpers';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/takeUntil';
import { OffboardingAction, OffboardingCase, OffboardingHistory, OffboardingStage } from '../../models/offboarding.model';
import { OffboardingService } from '../../services/offboarding.service';
import { ASSET } from '../common/employee-assets/asset.constants';
import { assetErrorKey } from '../common/employee-assets/asset.helpers';
import { OFFBOARDING as O } from './offboarding.constants';
import { OffboardingActionModalComponent } from './offboarding-action-modal.component';

@Component({selector: 'app-offboarding-detail', templateUrl: './offboarding-detail.component.html',
  styleUrls: ['../common/employee-assets/employee-assets.component.css', './offboarding-sequential.component.css',
    './offboarding-detail.component.css']})
export class OffboardingDetailComponent implements OnInit, OnDestroy {
  trackBy = workflowTrackBy;
  item: OffboardingCase;
  stageViews: StageView[] = [];
  currentStage: OffboardingStage;
  selectedStageKey = ASSET.empty;
  constants = O;
  caseActions: string[] = [];
  loading = false;
  errorKey = ASSET.empty;
  history: OffboardingHistory[] = [];
  historyError = ASSET.empty;
  historyOffset = ASSET.zero;
  historyMore = false;
  historyLoading = false;
  private request: Subscription;
  private historyRequest: Subscription;
  private dialog: NgbModalRef;
  private disposed = false;
  private generation = ASSET.zero;
  private destroyed = new Subject<void>();
  constructor(private api: OffboardingService, private route: ActivatedRoute, private modals: NgbModal) {}
  ngOnInit(): void {
    this.route.params.takeUntil(this.destroyed).subscribe(() => this.load());
  }
  ngOnDestroy(): void {
    this.disposed = true; this.generation++; this.closeDialog(); this.destroyed.next(); this.destroyed.complete();
  }
  load(): void {
    if (this.disposed) { return; }
    if (this.request) { this.request.unsubscribe(); }
    if (this.item && this.item.id !== this.route.snapshot.params[O.id]) { this.clearCase(); }
    const caseId = this.route.snapshot.params[O.id]; const generation = this.generation;
    this.loading = true; this.errorKey = ASSET.empty;
    this.request = this.api.get(caseId).takeUntil(this.destroyed).subscribe(result => {
      if (this.current(caseId, generation)) { this.applied(result); }
    }, error => {
      if (!this.current(caseId, generation)) { return; }
      this.loading = false; this.errorKey = assetErrorKey(error, false);
      if (this.errorKey === ASSET.deniedKey) { this.clearCase(); }
    });
  }
  applied(result: OffboardingCase): void {
    if (this.disposed) { return; }
    if (this.route.snapshot.params && this.route.snapshot.params[O.id] && result.id !== this.route.snapshot.params[O.id]) { return; }
    if (this.item && this.item.id === result.id && result.version < this.item.version) { return; }
    this.item = result; this.loading = false;
    this.resolveLabels(result);
    this.caseActions = result.allowedActions.filter(action => this.globalAction(action));
    this.projectStages(); this.loadHistory(false);
  }
  private globalAction(action: string): boolean {
    if (LABEL.caseActions.indexOf(action) < ASSET.zero) { return false; }
    if (action === O.cancel && (this.item.status === O.ceo || this.item.status === O.hr)) { return false; }
    return true;
  }
  private projectStages(): void {
    this.stageViews = (this.item.stages || []).map((stage, index, all) => {
      const completed = this.lastCompleted(stage);
      return {key: this.item.id + ASSET.slash + stage.key, stage: stage,
        finalStage: index === all.length - ASSET.one, approval: stage.function === LABEL.ceoFunction || stage.key === O.hr,
        completedAt: completed ? completed.completedAt : stage.key === O.ceoStage ? this.item.approvedAt : null,
        completedBy: completed ? completed.completedByName : stage.key === O.ceoStage ? this.item.approvedByName : null};
    });
    this.currentStage = (this.item.stages || []).filter(stage => stage.current)[ASSET.zero];
    this.resolveSelectedStage();
  }
  selectStage(key: string): void {
    if (this.stageViews.some(view => view.stage.key === key)) { this.selectedStageKey = key; }
  }
  get selectedStageIncomplete(): boolean {
    return this.stageViews.some(view => view.stage.key === this.selectedStageKey && view.stage.state !== O.completed);
  }
  get initializationPending(): boolean {
    return !!this.item && this.item.status === O.draft && this.item.initialized === false && !this.stageViews.length;
  }
  get collectionBlockers(): string[] {
    return this.item ? (this.item.blockers || []).filter(reason => reason.indexOf(O.collectionPrefix) === ASSET.zero || reason === O.openServiceAccess) : [];
  }
  private resolveSelectedStage(): void {
    const stages = this.item.stages || [];
    if (stages.some(stage => stage.key === this.selectedStageKey)) { return; }
    const query = this.route.snapshot.queryParams || {};
    const selected = stages.filter(stage => stage.key === query[O.stageQuery])[ASSET.zero] || this.currentStage || stages[ASSET.zero];
    this.selectedStageKey = selected ? selected.key : ASSET.empty;
  }
  private lastCompleted(stage: OffboardingStage): OffboardingAction {
    return (stage.actions || []).filter(row => !!row.completedAt).reduce((latest, row) =>
      !latest || row.completedAt > latest.completedAt ? row : latest, <OffboardingAction>null);
  }
  private resolveLabels(result: OffboardingCase): void {
    const names = result.actorNames || {};
    result.createdByName = names[result.createdBy];
    result.hrApprovedByName = names[result.hrApprovedBy];
    result.approvedByName = names[result.approvedBy];
    result.actions.forEach(row => this.actionNames(row, names));
    (result.stages || []).forEach(stage => (stage.actions || []).forEach(row => this.actionNames(row, names)));
  }
  private actionNames(row: OffboardingAction, names: {[id: string]: string}): void {
    row.assigneeName = names[row.assigneeId] || row.assigneeName;
    row.completedByName = names[row.completedBy] || row.completedByName;
  }
  act(action: string): void {
    if (this.disposed || this.dialog || this.loading || !this.item || this.item.allowedActions.indexOf(action) < ASSET.zero) { return; }
    const caseId = this.item.id; const generation = this.generation;
    if (!this.current(caseId, generation)) { return; }
    const ref = this.modals.open(OffboardingActionModalComponent,
      {size: ASSET.modalSize, beforeDismiss: () => !ref.componentInstance.busy});
    ref.componentInstance.item = this.item; ref.componentInstance.action = action;
    this.dialog = ref;
    const context = {caseId: caseId, generation: generation};
    ref.result.then(result => this.closed(ref, context, !!result), () => this.closed(ref, context, false));
  }
  loadHistory(more: boolean): void {
    if (this.disposed || more && this.historyLoading || !this.item) { return; }
    if (this.historyRequest) { this.historyRequest.unsubscribe(); }
    const caseId = this.item.id;
    const offset = more ? this.historyOffset + O.limitValue : ASSET.zero;
    this.historyError = ASSET.empty; this.historyLoading = true;
    this.historyRequest = this.api.history(caseId, offset).takeUntil(this.destroyed).subscribe(result => {
      if (!this.item || this.item.id !== caseId) { return; }
      this.history = more ? this.history.concat(result.items) : result.items; this.historyMore = result.hasMore;
      this.historyOffset = offset; this.historyLoading = false;
    }, error => {
      if (!this.item || this.item.id !== caseId) { return; }
      this.historyLoading = false; this.historyError = assetErrorKey(error, false);
    });
  }
  private clearHistory(): void {
    if (this.historyRequest) { this.historyRequest.unsubscribe(); this.historyRequest = null; }
    this.history = []; this.historyOffset = ASSET.zero; this.historyMore = false;
    this.historyLoading = false; this.historyError = ASSET.empty;
  }
  private clearCase(): void {
    this.generation++; this.closeDialog(); this.caseActions = [];
    this.clearHistory(); this.item = null; this.stageViews = [];
    this.currentStage = null; this.selectedStageKey = ASSET.empty;
  }
  private current(caseId: string, generation: number): boolean {
    const params = this.route.snapshot.params || {};
    return !this.disposed && generation === this.generation && (!params[O.id] || params[O.id] === caseId);
  }
  private closed(ref: NgbModalRef, context: CaseDialogContext, changed: boolean): void {
    if (this.dialog !== ref) { return; } this.dialog = null;
    if (changed && this.current(context.caseId, context.generation) && this.item && this.item.id === context.caseId) { this.load(); }
  }
  private closeDialog(): void {
    if (this.dialog) { const ref = this.dialog; this.dialog = null; ref.close(); }
  }
}
interface CaseDialogContext {caseId: string; generation: number;}
interface StageView {key: string; stage: OffboardingStage; finalStage: boolean; approval: boolean; completedAt?: string; completedBy?: string;}
const LABEL = {ceoFunction: 'CEO',
  caseActions: [O.proposeDates, O.hrDates, O.ceoDates, O.rejectDates, O.finalize, O.reconcileOrg, O.cancel]};



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-detail.component.ts